import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Button, ListItemIcon, ListItemText, Menu, MenuItem, Pagination, Skeleton, Stack,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { Add, BadgeOutlined, ContentCopyOutlined, DeleteOutline, EditOutlined, StarOutlineRounded } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { idCardService } from '../../services/idCardService';
import type { IdCardAudience, IdCardDesign } from '../../types/idCards';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import EmptyState from '../../components/common/EmptyState';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import { segmentedSx } from '../../components/admissions/tabLayout';
import { AUDIENCE_LABEL } from '../../components/idCards/catalog';
import { useCardContext } from '../../components/idCards/useCardContext';
import { usePreviewPerson } from '../../components/idCards/usePreviewPerson';
import DesignTile from '../../components/idCards/designs/DesignTile';
import DesignEditorDialog from '../../components/idCards/designs/DesignEditorDialog';
import { fmtDate } from '../../utils/pdf';

const PAGE_SIZE = 12;

/**
 * ID card designs — how the school's student and staff cards look. Each is
 * a layout, colours, the details shown and the back; the default design is
 * preselected when cards are printed.
 */
const IdCardDesignsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const writable = canWrite('id_card_designs');
  const [searchParams, setSearchParams] = useSearchParams();
  const audience: IdCardAudience = searchParams.get('for') === 'staff' ? 'staff' : 'student';

  const { school, activeSession, defaultValidUntil } = useCardContext();
  const { person, photo } = usePreviewPerson(audience, activeSession?.name);
  const validUntil = fmtDate(defaultValidUntil(audience));

  const [page, setPage] = useState(1);
  const [designs, setDesigns] = useState<IdCardDesign[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<{ design: IdCardDesign | null } | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; design: IdCardDesign } | null>(null);
  const [deleting, setDeleting] = useState<IdCardDesign | null>(null);
  const [busy, setBusy] = useState(false);

  const seq = useRef(0);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    try {
      const res = await idCardService.listDesigns(audience, { page, limit: PAGE_SIZE });
      if (mine !== seq.current) return;
      setDesigns(res.items);
      setTotalPages(res.pagination.totalPages);
    } catch (err: any) {
      if (mine === seq.current) enqueueSnackbar(err?.message || 'Could not load the designs', { variant: 'error' });
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [audience, page, enqueueSnackbar]);
  useEffect(() => { load(); }, [load]);

  const setAudience = (next: IdCardAudience) => {
    setPage(1);
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('for', next); return p; }, { replace: true });
  };

  const act = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await fn();
      enqueueSnackbar(done, { variant: 'success' });
      await load();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Something went wrong', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const openMenu = useCallback((anchor: HTMLElement, design: IdCardDesign) => setMenu({ anchor, design }), []);
  const openEditor = useCallback((design: IdCardDesign) => setEditing({ design }), []);
  const label = AUDIENCE_LABEL[audience].toLowerCase();
  const successor = deleting?.isDefault ? designs.find(d => d._id !== deleting._id) : undefined;

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }} sx={{ mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2 }}>ID card designs</Typography>
          <Typography variant="body2" color="text.secondary">
            How your school's ID cards look. The default design is chosen for you when cards are printed.
          </Typography>
        </Box>
        {writable && school && (
          <Button variant="contained" disableElevation startIcon={<Add />} onClick={() => setEditing({ design: null })} sx={{ textTransform: 'none', flexShrink: 0 }}>
            New {label} design
          </Button>
        )}
      </Stack>

      <ToggleButtonGroup exclusive size="small" value={audience} onChange={(_, v) => v && setAudience(v)} sx={[segmentedSx, { mb: 2.5 }]}>
        <ToggleButton value="student">Student cards</ToggleButton>
        <ToggleButton value="staff">Staff cards</ToggleButton>
      </ToggleButtonGroup>

      {loading && designs.length === 0 ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 2.5 }}>
          {[0, 1, 2].map(i => <Skeleton key={i} variant="rounded" height={262} sx={{ borderRadius: 3 }} />)}
        </Box>
      ) : designs.length === 0 ? (
        <EmptyState
          icon={<BadgeOutlined />}
          title={`No ${label} card design yet`}
          description={writable
            ? `Create one to start printing ${label} ID cards. It begins as a finished design you can adjust.`
            : `Ask someone who manages ID card designs to create one.`}
          action={writable && school ? { label: 'Create the first design', onClick: () => setEditing({ design: null }), icon: <Add /> } : undefined}
        />
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 2.5, opacity: loading ? 0.6 : 1, transition: 'opacity .2s' }}>
            {school && designs.map(d => (
              <DesignTile
                key={d._id}
                design={d}
                school={school}
                person={person}
                photo={photo}
                validUntil={validUntil}
                onOpen={openEditor}
                onMenu={writable ? openMenu : undefined}
              />
            ))}
          </Box>
          {totalPages > 1 && (
            <Stack alignItems="center" sx={{ mt: 3 }}>
              <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded" />
            </Stack>
          )}
        </>
      )}

      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
        {menu && [
          <MenuItem key="edit" onClick={() => { setEditing({ design: menu.design }); setMenu(null); }}>
            <ListItemIcon><EditOutlined fontSize="small" /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>,
          <MenuItem key="copy" disabled={busy} onClick={() => { const d = menu.design; setMenu(null); act(() => idCardService.duplicateDesign(d._id), `${d.name} duplicated`); }}>
            <ListItemIcon><ContentCopyOutlined fontSize="small" /></ListItemIcon>
            <ListItemText>Duplicate</ListItemText>
          </MenuItem>,
          !menu.design.isDefault && (
            <MenuItem key="default" disabled={busy} onClick={() => { const d = menu.design; setMenu(null); act(() => idCardService.makeDefault(d._id), `${d.name} is now the default`); }}>
              <ListItemIcon><StarOutlineRounded fontSize="small" /></ListItemIcon>
              <ListItemText primary="Make default" secondary="Chosen first when printing" />
            </MenuItem>
          ),
          <MenuItem key="delete" onClick={() => { setDeleting(menu.design); setMenu(null); }} sx={{ color: 'error.main' }}>
            <ListItemIcon><DeleteOutline fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      <ConfirmationDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => { const d = deleting!; setDeleting(null); act(() => idCardService.deleteDesign(d._id), `${d.name} deleted`); }}
        title={`Delete ${deleting?.name ?? 'this design'}?`}
        message={deleting?.isDefault
          ? successor ? `It is the default. ${successor.name} becomes the default instead.` : `It is the only ${label} design — you'll need a new one before printing ${label} cards.`
          : 'Cards already printed are not affected.'}
        confirmText="Delete"
        confirmColor="error"
        loading={busy}
      />

      {editing && school && (
        <DesignEditorDialog
          audience={audience}
          design={editing.design}
          school={school}
          validUntil={validUntil}
          person={person}
          photo={photo}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </Box>
  );
};

export default IdCardDesignsPage;
